import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { append, iif, insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { EntityTypeEnum } from './../../enums/entity-type.enum';
import { isDate1BeforeDate2 } from './../../utils/date.utils';

import { MonthStatusEnum } from 'src/shared/enums';
import { IAvailableEventsDTOIn, IBookedEventsByMonth, IEvent, IEventDTOIn, IMonthlyEventData } from '../../interfaces/event.interface';
import {
    IBookedMeal,
    IBookedMealsDTOIn,
    IMealMenuFile,
    IMealMenuFiles,
    IMealSetting,
    ISchoolMealsSettings,
    ITotalOfBookedMealsPerMonth,
} from '../../interfaces/meal.interface';
import { MappersService } from '../../services/mappers.service';
import { MealsService } from '../../services/meals.service';
import { getMonthTrad, parseDDMMYYYToDate } from '../../utils/date.utils';
import {
    ActivateMeals,
    BookMeals,
    CancelMealBooking,
    DeleteMealMenuFile,
    DisableMeals,
    DisableMealsFullMonth,
    GetAllAvailableMeals,
    GetMealMenuDatas,
    GetSchoolMealsSettings,
    GetTotalOfBookedMealsPerMonth,
    PostMealMenuFile,
    ResetMealsData,
    UpdateMealSetting,
} from '../actions/meals.action';
import { SetParentMealReservationInActiveMonth } from '../actions/orders.action';
import { GetBookedMealsPerMonth } from './../actions/meals.action';
import { AuthState } from './auth.state';
import { NurseryStateModel } from './nursery.state';

export interface MealsStateModel {
    schoolMealsSettings: IMealSetting[];
    isLoaded: boolean;
    mealsData: IMonthlyEventData[];
    bookedMeals: IBookedEventsByMonth[];
    filteredBookedMeals: IBookedMeal[];
}

@State<MealsStateModel>({
    name: 'meals',
    defaults: {
        schoolMealsSettings: [],
        isLoaded: false,
        mealsData: [],
        bookedMeals: [],
        filteredBookedMeals: [],
    },
})
@Injectable()
export class MealsState {
    constructor(private mealsService: MealsService, private store: Store, private mapperService: MappersService) {}

    @Selector()
    public static schoolMealsSettings(state: MealsStateModel): IMealSetting[] {
        return state.schoolMealsSettings;
    }

    @Selector()
    public static getIsMealsSettingsLoaded(state: NurseryStateModel): boolean {
        return state.isLoaded;
    }

    @Selector()
    public static mealsData(state: MealsStateModel): IMonthlyEventData[] {
        return state.mealsData;
    }

    @Selector()
    public static bookedMeals(state: MealsStateModel): IBookedEventsByMonth[] {
        return state.bookedMeals;
    }

    @Selector()
    public static filteredBookedMeals(state: MealsStateModel): IBookedMeal[] {
        return state.filteredBookedMeals;
    }

    @Selector()
    public static hasActiveMonth(state: MealsStateModel): boolean {
        return state.mealsData?.some((d: IMonthlyEventData) => d.status === MonthStatusEnum.OPEN);
    }

    @Action(GetSchoolMealsSettings)
    public getSchoolMealsSettings(ctx: StateContext<MealsStateModel>, action: GetSchoolMealsSettings): Observable<ISchoolMealsSettings> {
        return this.mealsService.getSchoolMealsSettings(action.schoolId).pipe(
            tap((result: ISchoolMealsSettings) => {
                if (result) {
                    ctx.patchState({
                        schoolMealsSettings: result.meal_settings,
                        isLoaded: true,
                    });
                }
            })
        );
    }

    @Action(UpdateMealSetting)
    public updateMealSetting(ctx: StateContext<MealsStateModel>, action: UpdateMealSetting): Observable<IMealSetting> {
        return this.mealsService.patchSchoolMealSetting(action.mealSetting).pipe(
            tap((result: IMealSetting) => {
                if (result) {
                    ctx.setState(
                        patch({
                            schoolMealsSettings: updateItem<IMealSetting>(
                                (mealSetting: IMealSetting) => mealSetting.id === action.mealSetting.meal_setting.id,
                                result
                            ),
                        })
                    );
                }
            })
        );
    }

    @Action(GetAllAvailableMeals)
    public getAllAvailableMeals(ctx: StateContext<MealsStateModel>): Observable<IMonthlyEventData[]> {
        const schoolId: number = this.store.selectSnapshot(AuthState.currentSchool).id;
        const selectedStudentId: number = this.store.selectSnapshot(AuthState.getStudentIdSelected);
        const currentUserType: EntityTypeEnum = this.store.selectSnapshot(AuthState.connectedUserType);

        if (currentUserType === EntityTypeEnum.PARENT && !selectedStudentId) {
            return null;
        }

        return this.mealsService.getAllAvailableMeals(schoolId, selectedStudentId).pipe(
            tap((result: IMonthlyEventData[]) => {
                if (result) {
                    ctx.patchState({
                        mealsData: result,
                    });
                }
            })
        );
    }

    @Action(ActivateMeals)
    public activateMeals(ctx: StateContext<MealsStateModel>, action: ActivateMeals): Observable<IAvailableEventsDTOIn> {
        const schoolId: number = this.store.selectSnapshot(AuthState.currentSchool).id;
        return this.mealsService.activateMeals(action.meals, schoolId).pipe(
            tap((result: IAvailableEventsDTOIn) => {
                if (result) {
                    result.meals.forEach((meal: IEventDTOIn) => {
                        const state: MealsStateModel = ctx.getState();
                        const mealDate: Date = parseDDMMYYYToDate(meal.date);
                        const monthIndex: number = mealDate.getMonth();
                        const minDays: number =
                            ctx.getState().schoolMealsSettings[0].min_days > ctx.getState().schoolMealsSettings[1].min_days
                                ? ctx.getState().schoolMealsSettings[0].min_days
                                : ctx.getState().schoolMealsSettings[1].min_days;
                        const maxActivationDate: Date = new Date();
                        maxActivationDate.setDate(maxActivationDate.getDate() + minDays);
                        const isMealInBookingDelay: boolean =
                            isDate1BeforeDate2(mealDate, maxActivationDate) && isDate1BeforeDate2(new Date(), mealDate);
                        const currentMealStatus: MonthStatusEnum = state.mealsData.find((m: IMonthlyEventData) => m.month === monthIndex)
                            .status;
                        ctx.setState(
                            patch({
                                mealsData: updateItem<IMonthlyEventData>(
                                    (monthData: IMonthlyEventData) => monthData.month === monthIndex,
                                    patch({
                                        status: isMealInBookingDelay ? MonthStatusEnum.OPEN : currentMealStatus,
                                        meals: append<IEvent>([{ id: meal.id, date: mealDate }] as IEvent[]),
                                    })
                                ),
                            })
                        );
                    });
                }
            })
        );
    }

    @Action(DisableMeals)
    public disableMeals(ctx: StateContext<MealsStateModel>, action: DisableMeals): Observable<void> {
        const schoolId: number = this.store.selectSnapshot(AuthState.currentSchool).id;
        return this.mealsService.disableMeals(action.meals, schoolId).pipe(
            tap(() => {
                action.meals.meal.dates.forEach((date: string) => {
                    const state: MealsStateModel = ctx.getState();
                    const monthIndex: number = parseDDMMYYYToDate(date).getMonth();
                    const isLastMeals: boolean = state.mealsData?.find((m) => m.month === monthIndex).meals?.length === 1;
                    const currentMealStatus: MonthStatusEnum = state.mealsData.find((m: IMonthlyEventData) => m.month === monthIndex)
                        .status;
                    ctx.setState(
                        patch({
                            mealsData: updateItem<IMonthlyEventData>(
                                (monthData: IMonthlyEventData) => monthData.month === monthIndex,
                                patch({
                                    status: isLastMeals ? MonthStatusEnum.INACTIVE : currentMealStatus,
                                    meals: removeItem<IEvent>(
                                        (meal: IEvent) => meal.date.toLocaleDateString() === parseDDMMYYYToDate(date).toLocaleDateString()
                                    ),
                                })
                            ),
                        })
                    );
                });
            })
        );
    }

    @Action(DisableMealsFullMonth)
    public disableMealsFullMonth(ctx: StateContext<MealsStateModel>, action: DisableMealsFullMonth): Observable<void> {
        const schoolId: number = this.store.selectSnapshot(AuthState.currentSchool).id;
        return this.mealsService.disableFullMonthMeals(action.monthDate, schoolId).pipe(
            tap(() => {
                const monthIndex: number = action.monthDate.getMonth();
                ctx.setState(
                    patch({
                        mealsData: updateItem<IMonthlyEventData>(
                            (monthData: IMonthlyEventData) => monthData.month === monthIndex,
                            patch({
                                status: MonthStatusEnum.INACTIVE,
                                meals: [] as IEvent[],
                            })
                        ),
                    })
                );
            })
        );
    }

    @Action(ResetMealsData)
    public resetMealsData(ctx: StateContext<MealsStateModel>): void {
        ctx.patchState({
            mealsData: null,
            isLoaded: false,
        });
    }

    @Action(BookMeals)
    public bookMeals(ctx: StateContext<MealsStateModel>, action: BookMeals): Observable<IBookedMealsDTOIn> {
        const selectedStudentId: number = this.store.selectSnapshot(AuthState.getStudentIdSelected);
        const state: MealsStateModel = ctx.getState();
        return this.mealsService.bookMeals(action.mealsToBook, selectedStudentId).pipe(
            tap((result: IBookedMealsDTOIn) => {
                const newBookedMealsByMonth: IBookedEventsByMonth[] = this.mapperService.fromBookedMealsToBookedMealsByMonth(
                    result.book_meals
                );
                newBookedMealsByMonth.forEach((monthData: IBookedEventsByMonth) => {
                    if (monthData.bookedMeals.length > 0) {
                        ctx.setState(
                            patch({
                                bookedMeals: updateItem<IBookedEventsByMonth>(
                                    (month: IBookedEventsByMonth) => month.monthNumber === monthData.monthNumber,
                                    patch({
                                        bookedMeals: append(monthData.bookedMeals),
                                    })
                                ),
                            })
                        );
                        // Si réservation dans le dernier mois actif,
                        // on met à jour le status des réservations de mois actif dans Order State
                        // (ce qui mettra à jour le "new" dans la side-nav)
                        if (
                            state.mealsData.find(
                                (d: IMonthlyEventData) => d.month === monthData.monthNumber && d.status === MonthStatusEnum.OPEN
                            ) &&
                            state.mealsData.find(
                                (d: IMonthlyEventData) =>
                                    (d.month === monthData.monthNumber + 1 || (d.month === 0 && monthData.monthNumber === 11)) &&
                                    (d.status === MonthStatusEnum.INACTIVE || d.status === MonthStatusEnum.CLOSED)
                            )
                        ) {
                            this.store.dispatch(new SetParentMealReservationInActiveMonth());
                        }
                    }
                });
            })
        );
    }

    @Action(GetBookedMealsPerMonth)
    public getBookedMealsPerMonth(ctx: StateContext<MealsStateModel>, action: GetBookedMealsPerMonth): Observable<IBookedMealsDTOIn> {
        const selectedStudentId: number = this.store.selectSnapshot(AuthState.getStudentIdSelected);
        return this.mealsService.getBookedMeals({ ...action.payload, selectedStudentId }).pipe(
            tap((result: IBookedMealsDTOIn) => {
                const newBookedMealsByMonth: IBookedEventsByMonth[] = this.mapperService.fromBookedMealsToBookedMealsByMonth(
                    result.book_meals
                );
                if (action.isFilter) {
                    const filteredMeals: IBookedMeal[] = [];
                    newBookedMealsByMonth.forEach((month: IBookedEventsByMonth) => {
                        Array.prototype.push.apply(filteredMeals, month.bookedMeals);
                    });
                    ctx.patchState({
                        filteredBookedMeals: filteredMeals,
                    });
                } else {
                    newBookedMealsByMonth.forEach((monthData: IBookedEventsByMonth) => {
                        if (monthData.bookedMeals.length > 0) {
                            ctx.setState(
                                patch({
                                    bookedMeals: updateItem<IBookedEventsByMonth>(
                                        (event: IBookedEventsByMonth) => event.monthNumber === monthData.monthNumber,
                                        patch({ ...event, bookedMeals: monthData.bookedMeals })
                                    ),
                                })
                            );
                        }
                    });
                }
            })
        );
    }

    @Action(GetTotalOfBookedMealsPerMonth)
    public getTotalOfBookedMealsPerMonth(ctx: StateContext<MealsStateModel>): Observable<ITotalOfBookedMealsPerMonth[]> {
        const selectedStudentId: number = this.store.selectSnapshot(AuthState.getStudentIdSelected);
        return this.mealsService.getTotalOfBookedMealsPerMonth(selectedStudentId).pipe(
            tap((result: ITotalOfBookedMealsPerMonth[]) => {
                if (result) {
                    ctx.patchState({
                        bookedMeals: result.map((month: ITotalOfBookedMealsPerMonth) => ({
                            monthNumber: month.month - 1,
                            monthName: getMonthTrad(month.month - 1),
                            bookedMeals: [],
                            eventsCount: {
                                total: month.meals_count,
                                primary: month.primary_meals_count,
                                kindergarten: month.kindergarten_meals_count,
                            },
                        })),
                    });
                }
            })
        );
    }

    @Action(CancelMealBooking)
    public cancelMealBooking(ctx: StateContext<MealsStateModel>, action: CancelMealBooking): Observable<void> {
        return this.mealsService.cancelMeal(action.idsToCancel).pipe(
            tap(() => {
                action.idsToCancel.book_meal_ids.forEach((idToCancel: number) => {
                    ctx.setState(
                        patch({
                            bookedMeals: updateItem<IBookedEventsByMonth>(
                                (month: IBookedEventsByMonth) =>
                                    month.bookedMeals.findIndex((toFind: IBookedMeal) => toFind.id === idToCancel) > -1,
                                patch({
                                    bookedMeals: removeItem((meal: IBookedMeal) => meal.id === idToCancel),
                                })
                            ),
                        })
                    );
                });
            })
        );
    }

    @Action(PostMealMenuFile)
    public postMealMenuFile(ctx: StateContext<MealsStateModel>, action: PostMealMenuFile): Observable<IMealMenuFile> {
        const schoolId: number = this.store.selectSnapshot(AuthState.currentSchool).id;
        return this.mealsService.postMealMenuFile(schoolId, action.date, action.file).pipe(
            tap((result: IMealMenuFile) => {
                if (result) {
                    const date: Date = new Date(result.date);
                    ctx.setState(
                        patch({
                            mealsData: updateItem<IMonthlyEventData>(
                                (month: IMonthlyEventData) => month.month === date.getMonth(),
                                patch({
                                    menuFilePath: result.file.file_path,
                                })
                            ),
                        })
                    );
                }
            })
        );
    }

    @Action(DeleteMealMenuFile)
    public deleteMealMenuFile(ctx: StateContext<MealsStateModel>, action: DeleteMealMenuFile): Observable<void> {
        const schoolId: number = this.store.selectSnapshot(AuthState.currentSchool).id;
        return this.mealsService.deleteMealMenuFile(schoolId, action.date).pipe(
            tap(() => {
                ctx.setState(
                    patch({
                        mealsData: updateItem<IMonthlyEventData>(
                            (month: IMonthlyEventData) => month.month === action.date.getMonth(),
                            patch({
                                menuFilePath: null,
                            })
                        ),
                    })
                );
            })
        );
    }

    @Action(GetMealMenuDatas)
    public getMealMenuDatas(ctx: StateContext<MealsStateModel>): Observable<IMealMenuFiles> {
        const schoolId: number = this.store.selectSnapshot(AuthState.currentSchool).id;
        return this.mealsService.getMealMenuDatas(schoolId).pipe(
            tap((result: IMealMenuFiles) => {
                if (result) {
                    result.meal_files
                        .filter((mealData: IMealMenuFile) => {
                            const date: Date = new Date(mealData.date);
                            const currentDate: Date = new Date();
                            if (currentDate.getMonth() >= 8) {
                                return date.getMonth() >= 8
                                    ? date.getFullYear() === currentDate.getFullYear()
                                    : date.getFullYear() === currentDate.getFullYear() + 1;
                            } else {
                                return date.getMonth() >= 8
                                    ? date.getFullYear() === currentDate.getFullYear() - 1
                                    : date.getFullYear() === currentDate.getFullYear();
                            }
                        })
                        .forEach((mealData: IMealMenuFile) => {
                            const date: Date = new Date(mealData.date);
                            ctx.setState(
                                patch({
                                    mealsData: updateItem<IMonthlyEventData>(
                                        (month: IMonthlyEventData) => month.month === date.getMonth(),
                                        patch({
                                            menuFilePath: mealData.file.file_path,
                                        })
                                    ),
                                })
                            );
                        });
                }
            })
        );
    }

    private insertOrUpdateEvents(monthNumber: number, bookedEvent: IBookedEventsByMonth, events: IBookedEventsByMonth[]): any {
        return iif<IBookedEventsByMonth[]>(
            events.some((event: IBookedEventsByMonth) => event.monthNumber === monthNumber),
            updateItem<IBookedEventsByMonth>(
                (event: IBookedEventsByMonth) => event.monthNumber === monthNumber,
                patch({ ...event, bookedMeals: bookedEvent.bookedMeals })
            ),
            insertItem<IBookedEventsByMonth>(bookedEvent)
        );
    }
}
